import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import axios from "axios";
import { isYoungLearnerGrade } from "../../utils/gradeUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Proactive Instructor Context
 * Manages DOM scraping, TTS sync, and examination firewall
 */
export const ProactiveInstructorContext = createContext(null);

/**
 * Custom hook for DOM content extraction
 * Scrapes visible lesson content and tracks scroll position
 */
export function useDOMContentScraper(containerRef, isEnabled = true) {
  const [scrapedContent, setScrapedContent] = useState("");
  const [currentSection, setCurrentSection] = useState(null);
  const [visibleParagraphs, setVisibleParagraphs] = useState([]);
  const lastScrapedRef = useRef("");

  // Scrape content from DOM
  const scrapeContent = useCallback(() => {
    if (!containerRef?.current || !isEnabled) return;

    const container = containerRef.current;
    
    // Get all text content from lesson sections
    const sections = container.querySelectorAll('[data-testid="lesson-content-section"], .card');
    const paragraphs = [];
    let fullContent = "";

    sections.forEach((section, idx) => {
      // Get text from ReactMarkdown rendered content and other text
      const textContent = section.innerText || section.textContent || "";
      if (textContent.trim().length > 10) {
        fullContent += textContent + "\n\n";
        
        // Track individual paragraphs for highlighting
        const pElements = section.querySelectorAll('p, li, h3, h4');
        pElements.forEach((p, pIdx) => {
          const text = p.innerText || p.textContent;
          if (text && text.trim().length > 5) {
            paragraphs.push({
              id: `para-${idx}-${pIdx}`,
              text: text.trim(),
              element: p,
              sectionIndex: idx
            });
          }
        });
      }
    });

    // Only update if content changed
    if (fullContent !== lastScrapedRef.current) {
      lastScrapedRef.current = fullContent;
      setScrapedContent(fullContent.trim());
      setVisibleParagraphs(paragraphs);
    }

    return fullContent.trim();
  }, [containerRef, isEnabled]);

  // Track which section is currently visible (for persistent state)
  const trackVisibleSection = useCallback(() => {
    if (!containerRef?.current) return;

    const container = containerRef.current;
    const sections = container.querySelectorAll('.card');
    const containerRect = container.getBoundingClientRect();
    const viewportMiddle = window.innerHeight / 2;

    let closestSection = null;
    let closestDistance = Infinity;

    sections.forEach((section, idx) => {
      const rect = section.getBoundingClientRect();
      const sectionMiddle = rect.top + rect.height / 2;
      const distance = Math.abs(sectionMiddle - viewportMiddle);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestSection = {
          index: idx,
          element: section,
          title: section.querySelector('h3')?.innerText || `Section ${idx + 1}`
        };
      }
    });

    if (closestSection) {
      setCurrentSection(closestSection);
    }
  }, [containerRef]);

  // Set up scroll listener for content tracking
  useEffect(() => {
    if (!isEnabled) return;

    // Initial scrape
    const timer = setTimeout(() => {
      scrapeContent();
      trackVisibleSection();
    }, 500);

    // Scroll listener
    const handleScroll = () => {
      trackVisibleSection();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isEnabled, scrapeContent, trackVisibleSection]);

  // Rescrape when content might have changed
  const rescrape = useCallback(() => {
    scrapeContent();
  }, [scrapeContent]);

  return {
    scrapedContent,
    currentSection,
    visibleParagraphs,
    rescrape,
    scrapeContent
  };
}

/**
 * Examination Firewall Hook
 * Detects quiz/test pages and enforces reader-only mode
 */
export function useExaminationFirewall() {
  const [isExamMode, setIsExamMode] = useState(false);
  const [examType, setExamType] = useState(null); // 'quiz', 'test', 'exam'
  const [canReadAloud, setCanReadAloud] = useState(true);

  // Check URL for exam patterns
  useEffect(() => {
    const checkForExamMode = () => {
      const path = window.location.pathname.toLowerCase();
      const hash = window.location.hash.toLowerCase();
      const fullPath = path + hash;

      // Detect exam/quiz/test patterns - only when we're ON a quiz/exam page
      // Must be a route like /quiz/123 or #quiz, not just containing the word
      const quizRoutePattern = /\/(quiz|quizzes)($|\/|\?)/;
      const testRoutePattern = /\/(test|tests)($|\/|\?)/;
      const examRoutePattern = /\/(exam|exams|chapter-exam|final-exam)($|\/|\?)/;
      
      const isQuiz = quizRoutePattern.test(path) || hash.includes('#quiz');
      const isTest = testRoutePattern.test(path) || hash.includes('#test');
      const isExam = examRoutePattern.test(path) || hash.includes('#exam');

      if (isQuiz || isTest || isExam) {
        setIsExamMode(true);
        setExamType(isQuiz ? 'quiz' : isTest ? 'test' : 'exam');
        setCanReadAloud(true); // Can still read questions aloud
      } else {
        setIsExamMode(false);
        setExamType(null);
        setCanReadAloud(true);
      }
    };

    checkForExamMode();

    // Listen for navigation changes
    window.addEventListener("popstate", checkForExamMode);
    window.addEventListener("hashchange", checkForExamMode);

    return () => {
      window.removeEventListener("popstate", checkForExamMode);
      window.removeEventListener("hashchange", checkForExamMode);
    };
  }, []);

  // Also check DOM for quiz/exam elements (only when actually INSIDE a quiz/exam taking flow)
  useEffect(() => {
    const checkForQuizElements = () => {
      // Check for QuizTaker component data-testid elements
      const quizTakerElement = document.querySelector('[data-testid="quiz-taker"]');
      const quizResultElement = document.querySelector('[data-testid="quiz-result"]');
      const quizLoadingElement = document.querySelector('[data-testid="quiz-loading"]');
      
      // Check for quiz question options (more specific)
      const quizOptionElements = document.querySelectorAll('[data-testid^="q"][data-testid*="-option"]');
      
      // Also check for submit quiz button
      const submitQuizBtn = document.querySelector('[data-testid="submit-quiz-btn"]');
      
      // We're in quiz mode if any of these elements exist
      const inQuizMode = (
        quizTakerElement || 
        quizResultElement || 
        quizLoadingElement ||
        quizOptionElements.length > 0 ||
        submitQuizBtn
      );
      
      if (inQuizMode && !isExamMode) {
        setIsExamMode(true);
        setExamType('quiz');
      } else if (!inQuizMode && isExamMode) {
        // Only reset if we were in exam mode due to DOM detection
        // (URL-based detection should persist)
        const path = window.location.pathname.toLowerCase();
        const hash = window.location.hash.toLowerCase();
        const hasExamRoute = /\/(quiz|test|exam)/i.test(path) || /#(quiz|test|exam)/i.test(hash);
        
        if (!hasExamRoute) {
          setIsExamMode(false);
          setExamType(null);
        }
      }
    };

    // Initial check
    checkForQuizElements();
    
    // Set up mutation observer for DOM changes
    const observer = new MutationObserver(checkForQuizElements);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isExamMode]);

  return {
    isExamMode,
    examType,
    canReadAloud,
    // Firewall message for when user tries forbidden actions
    firewallMessage: isExamMode 
      ? "I'm in Reader Mode during exams. I can read questions aloud, but I cannot provide answers, hints, or explanations. You've got this!"
      : null
  };
}

/**
 * Word-by-Word TTS Sync Hook
 * Synchronizes TTS audio with karaoke-style highlighting
 */
export function useWordByWordTTS(personaVoice = "nova") {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [words, setWords] = useState([]);
  const [audioUrl, setAudioUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [estimatedDuration, setEstimatedDuration] = useState(0);
  
  const audioRef = useRef(null);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);

  // Parse text into words
  const parseWords = useCallback((text) => {
    if (!text) return [];
    // Split into words, keeping punctuation attached
    const wordPattern = /\S+/g;
    const matches = text.match(wordPattern) || [];
    return matches.map((word, idx) => ({
      word,
      index: idx,
      startTime: null, // Will be estimated
      endTime: null
    }));
  }, []);

  // Estimate word timings based on TTS speed (~150 words per minute)
  const estimateWordTimings = useCallback((wordList) => {
    const wordsPerMinute = 150;
    const msPerWord = (60 * 1000) / wordsPerMinute;
    
    let currentTime = 0;
    return wordList.map((wordObj, idx) => {
      // Adjust timing based on word length and punctuation
      const word = wordObj.word;
      let duration = msPerWord;
      
      // Longer words take slightly more time
      if (word.length > 8) duration *= 1.2;
      if (word.length > 12) duration *= 1.3;
      
      // Punctuation adds pauses
      if (word.endsWith('.') || word.endsWith('!') || word.endsWith('?')) {
        duration *= 1.5;
      } else if (word.endsWith(',') || word.endsWith(';') || word.endsWith(':')) {
        duration *= 1.2;
      }
      
      const startTime = currentTime;
      currentTime += duration;
      
      return {
        ...wordObj,
        startTime,
        endTime: currentTime
      };
    });
  }, []);

  // Generate TTS and start playback with sync
  const generateAndPlay = useCallback(async (text, voice = personaVoice) => {
    if (!text || text.trim().length === 0) return;

    try {
      setIsPlaying(false);
      setCurrentWordIndex(-1);
      setProgress(0);

      // Parse and time words
      const parsedWords = parseWords(text);
      const timedWords = estimateWordTimings(parsedWords);
      setWords(timedWords);

      // Calculate estimated duration
      const duration = timedWords.length > 0 
        ? timedWords[timedWords.length - 1].endTime 
        : 0;
      setEstimatedDuration(duration);

      // Get TTS audio from backend
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/classroom/tts`,
        null,
        {
          params: { text: text.substring(0, 4000), voice },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.audio_url) {
        setAudioUrl(response.data.audio_url);
        
        // Play audio
        if (audioRef.current) {
          audioRef.current.src = response.data.audio_url;
          await audioRef.current.play();
          setIsPlaying(true);
          startTimeRef.current = Date.now();
          
          // Start word tracking animation
          animateWordHighlight(timedWords);
        }
      }
    } catch (err) {
      console.error("TTS generation error:", err);
    }
  }, [personaVoice, parseWords, estimateWordTimings]);

  // Animate word highlights synced to audio
  const animateWordHighlight = useCallback((timedWords) => {
    const animate = () => {
      if (!startTimeRef.current || !audioRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      
      // Find current word based on elapsed time
      let currentIdx = -1;
      for (let i = 0; i < timedWords.length; i++) {
        if (elapsed >= timedWords[i].startTime && elapsed < timedWords[i].endTime) {
          currentIdx = i;
          break;
        }
      }

      // If we've passed all words, check if audio is still playing
      if (currentIdx === -1 && elapsed > 0) {
        const lastWord = timedWords[timedWords.length - 1];
        if (lastWord && elapsed < lastWord.endTime + 500) {
          currentIdx = timedWords.length - 1;
        }
      }

      setCurrentWordIndex(currentIdx);
      
      // Calculate progress
      if (timedWords.length > 0) {
        const totalDuration = timedWords[timedWords.length - 1].endTime;
        setProgress(Math.min((elapsed / totalDuration) * 100, 100));
      }

      // Continue animation if playing
      if (audioRef.current && !audioRef.current.paused) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  // Pause playback
  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPaused(true);
      setIsPlaying(false);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  }, []);

  // Resume playback
  const resume = useCallback(() => {
    if (audioRef.current && isPaused) {
      audioRef.current.play();
      setIsPaused(false);
      setIsPlaying(true);
      startTimeRef.current = Date.now() - (progress / 100) * estimatedDuration;
      animateWordHighlight(words);
    }
  }, [isPaused, progress, estimatedDuration, words, animateWordHighlight]);

  // Stop playback
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentWordIndex(-1);
    setProgress(0);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, []);

  // Handle audio end
  const handleAudioEnd = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentWordIndex(-1);
    setProgress(100);
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    isPlaying,
    isPaused,
    currentWordIndex,
    words,
    progress,
    audioUrl,
    audioRef,
    generateAndPlay,
    pause,
    resume,
    stop,
    handleAudioEnd
  };
}

/**
 * KaraokeText Component
 * Renders text with word-by-word highlighting synced to TTS
 */
export function KaraokeText({
  text,
  currentWordIndex,
  highlightColor = "#FFD700",
  style = {}
}) {
  const [words, setWords] = useState([]);
  const containerRef = useRef(null);

  // Parse text into words
  useEffect(() => {
    if (!text) {
      setWords([]);
      return;
    }
    const wordPattern = /\S+/g;
    const matches = text.match(wordPattern) || [];
    setWords(matches);
  }, [text]);

  // Auto-scroll to keep highlighted word visible
  useEffect(() => {
    if (currentWordIndex > 0 && containerRef.current) {
      const highlightedSpan = containerRef.current.querySelector(`[data-word-idx="${currentWordIndex}"]`);
      if (highlightedSpan) {
        highlightedSpan.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      }
    }
  }, [currentWordIndex]);

  if (!text || words.length === 0) return null;

  return (
    <div
      ref={containerRef}
      style={{
        lineHeight: "2",
        fontSize: "1.15rem",
        color: "#ddd",
        ...style
      }}
    >
      {words.map((word, idx) => (
        <span
          key={idx}
          data-word-idx={idx}
          style={{
            display: "inline",
            padding: "2px 4px",
            margin: "0 1px",
            borderRadius: "4px",
            transition: "all 0.1s ease-out",
            background: currentWordIndex === idx ? highlightColor : "transparent",
            color: currentWordIndex === idx ? "#000" : "inherit",
            fontWeight: currentWordIndex === idx ? "bold" : "normal",
            transform: currentWordIndex === idx ? "scale(1.05)" : "scale(1)",
            boxShadow: currentWordIndex === idx ? `0 2px 8px ${highlightColor}80` : "none"
          }}
        >
          {word}{" "}
        </span>
      ))}
    </div>
  );
}

/**
 * ProactiveInstructorProvider
 * Provides context for proactive instructor features
 */
export function ProactiveInstructorProvider({ children, gradeLevel }) {
  const [lessonContent, setLessonContent] = useState("");
  const [isTeachingMode, setIsTeachingMode] = useState(false);
  const [teachingProgress, setTeachingProgress] = useState(0);
  const [currentParagraphIndex, setCurrentParagraphIndex] = useState(0);
  const [paragraphs, setParagraphs] = useState([]);
  
  const firewall = useExaminationFirewall();
  
  // Determine if TTS should be mandatory (K-2) or optional (Advanced).
  // FIXED 2026-04-25: was substring-matching `.includes('1')` / `.includes('2')`
  // which falsely matched Grades 11/12/9-12.
  const isTTSMandatory = isYoungLearnerGrade(gradeLevel);

  // Parse content into teachable paragraphs
  const parseForTeaching = useCallback((content) => {
    if (!content) return [];
    
    // Split by paragraphs (double newlines or single newlines with length)
    const rawParagraphs = content.split(/\n\n+/).filter(p => p.trim().length > 20);
    
    return rawParagraphs.map((p, idx) => ({
      id: `teach-para-${idx}`,
      text: p.trim(),
      index: idx,
      taught: false
    }));
  }, []);

  // Update lesson content and parse for teaching
  const ingestContent = useCallback((content) => {
    setLessonContent(content);
    const parsed = parseForTeaching(content);
    setParagraphs(parsed);
    setCurrentParagraphIndex(0);
    setTeachingProgress(0);
  }, [parseForTeaching]);

  // Move to next paragraph
  const nextParagraph = useCallback(() => {
    if (currentParagraphIndex < paragraphs.length - 1) {
      setCurrentParagraphIndex(prev => prev + 1);
      setTeachingProgress(((currentParagraphIndex + 1) / paragraphs.length) * 100);
    }
  }, [currentParagraphIndex, paragraphs.length]);

  // Mark current paragraph as taught
  const markTaught = useCallback(() => {
    setParagraphs(prev => prev.map((p, idx) => 
      idx === currentParagraphIndex ? { ...p, taught: true } : p
    ));
    nextParagraph();
  }, [currentParagraphIndex, nextParagraph]);

  // Get current paragraph to teach
  const currentParagraph = paragraphs[currentParagraphIndex] || null;

  // Context value
  const value = {
    // Content
    lessonContent,
    paragraphs,
    currentParagraph,
    currentParagraphIndex,
    
    // Teaching state
    isTeachingMode,
    setIsTeachingMode,
    teachingProgress,
    
    // Actions
    ingestContent,
    nextParagraph,
    markTaught,
    
    // Examination firewall
    ...firewall,
    
    // Grade-specific settings
    isTTSMandatory,
    gradeLevel
  };

  return (
    <ProactiveInstructorContext.Provider value={value}>
      {children}
    </ProactiveInstructorContext.Provider>
  );
}

// Hook to use the proactive instructor context
export function useProactiveInstructor() {
  const context = useContext(ProactiveInstructorContext);
  if (!context) {
    throw new Error("useProactiveInstructor must be used within ProactiveInstructorProvider");
  }
  return context;
}

export default ProactiveInstructorProvider;
